import { useEffect, useRef, useState } from 'react';
import { Button, Loader } from '@scuf/common';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { USER_MANAGER } from 'resources/links';
import { notificationAtom } from 'resources';
import useUser from 'helpers/hooks/useUser';
import useFetch from 'helpers/hooks/useFetch';

import ModalMensagemPortal from './modal/modalMensagemPortal';
import FilesTable from './messageTable';

interface ManualFile {
    id: number;
    displayName: string;
    storedFileName: string;
    section: string;
}

interface FileContentProps {
    section: string;
}

function PortalMessage({ section }: FileContentProps): JSX.Element {
    const { t } = useTranslation();
    const { user } = useUser();

    const setNotification = useSetRecoilState(notificationAtom);

    const [files, setFiles] = useState<ManualFile[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isValid, setIsValid] = useState(false);
    const [screen, setScreen] = useState<'list' | 'upload'>('list');

    const [homePageFiles, setHomePageFiles] = useState<ManualFile[]>([]);
    const [repairCenterFiles, setRepairCenterFiles] = useState<ManualFile[]>([]);
    const [sparePartsFiles, setSparePartsFiles] = useState<ManualFile[]>([]);
    const [hslFiles, setHslFiles] = useState<ManualFile[]>([]);
    const [manufaturaFiles, setManufaturaFiles] = useState<ManualFile[]>([]);
    const [bslBrFiles, setBslBrFiles] = useState<ManualFile[]>([]);

    const [customDisplayName, setCustomDisplayName] = useState<string>('');

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedSection, setSelectedSection] = useState<string>('');
    const [selectedLanguage, setSelectedLanguage] = useState<string>('');

    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const isEditable = user?.role === '1' || user?.role === '2';

    const [, fetchFiles] = useFetch<void, ManualFile[]>({
        url: `${USER_MANAGER.getMensagensPortal}`,
        method: 'GET',
        onError: t('manual.fetchFilesError')
    });

    const [, deleteFile] = useFetch<{ id: number }>({
        url: USER_MANAGER.deleteManualFile,
        method: 'DELETE',
        onError: t('manual.deleteFileError')
    });

    useEffect(() => {
        setIsValid(
            selectedFile !== null &&
            selectedLanguage !== '' &&
            selectedSection !== '' &&
            customDisplayName.trim() !== ''
        );
    }, [
        selectedFile,
        selectedLanguage,
        selectedSection,
        customDisplayName
    ]);

    const loadFiles = async () => {
        setLoading(true);

        try {
            await fetchFiles(undefined, response => {
                const receivedFiles = response?.data ?? [];
                const homeFiles = receivedFiles.filter(
                    file => file.section?.toLowerCase().includes("msg-home")
                );
                const repairFiles = receivedFiles.filter(
                    file => file.section?.toLowerCase().includes("msg-repair")
                );
                const spareFiles = receivedFiles.filter(
                    file => file.section?.toLowerCase().includes("msg-spare")
                );
                const hslFiles = receivedFiles.filter(
                    file => file.section?.toLowerCase().includes("msg-hsl")
                );
                const manufaturaFiles = receivedFiles.filter(
                    file => file.section?.toLowerCase().includes("msg-manufatura")
                );
                const bslBrFiles = receivedFiles.filter(
                    file => file.section?.toLowerCase().includes("msg-bslbr")
                );
                setFiles(receivedFiles);
                setHomePageFiles(homeFiles);
                setRepairCenterFiles(repairFiles);
                setSparePartsFiles(spareFiles);
                setHslFiles(hslFiles);
                setManufaturaFiles(manufaturaFiles);
                setBslBrFiles(bslBrFiles);
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFiles();
    }, [section]);

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (id: number) => {
        setDeletingId(id);

        try {
            await deleteFile({ id });

            setFiles(previousFiles =>
                previousFiles.filter(file => file.id !== id)
            );

            setNotification(previousNotification => ({
                ...previousNotification,
                message: t('manual.deleteSuccess'),
                type: 'success'
            }));
        } catch {
            setNotification(previousNotification => ({
                ...previousNotification,
                message: t('manual.deleteError'),
                type: 'critical'
            }));
        } finally {
            setDeletingId(null);
            refresh();
        }
    };

    const handleDownload = async (
        id: number,
        displayName: string,
        storedFileName: string
    ) => {
        try {
            setDownloadingId(id);

            const response = await fetch(
                `${USER_MANAGER.downloadManualFileById}?id=${id}`,
                {
                    method: 'GET',
                    headers: {
                        Accept: 'application/octet-stream'
                    },
                    credentials: 'include'
                }
            );

            if (!response.ok) {
                throw new Error(`Download error: ${response.status}`);
            }

            const blob = await response.blob();

            const extension = storedFileName.includes('.')
                ? storedFileName.slice(storedFileName.lastIndexOf('.'))
                : '';

            const finalName =
                extension && !displayName.toLowerCase().endsWith(extension.toLowerCase())
                    ? `${displayName}${extension}`
                    : displayName;

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', finalName);

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch {
            setNotification(previousNotification => ({
                ...previousNotification,
                message: t('manual.downloadError'),
                type: 'critical'
            }));
        } finally {
            setDownloadingId(null);
        }
    };

    const refresh = async () => {
        await loadFiles();
    };

    const openUploadScreen = () => {
        setCustomDisplayName('');
        setSelectedFile(null);
        resetFileInput();
        setScreen('upload');
    };

    const backToList = () => {
        setCustomDisplayName('');
        setSelectedFile(null);
        resetFileInput();
        setScreen('list');
    };

    const downloadFile = (file: ManualFile) => {
        handleDownload(
            file.id,
            file.displayName,
            file.storedFileName
        );
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        const file = selectedFile;
        if (!file || !customDisplayName.trim()) return;

        const formData = new FormData();
        formData.append('formFile', file);
        formData.append('displayName', customDisplayName);
        formData.append('ssoId', user?.ssoid ?? '');
        formData.append('language', selectedLanguage);
        formData.append('section', selectedSection);

        try {
            setLoading(true);
            const response = await axios.post(USER_MANAGER.uploadMensagemPortal, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    //Authorization: `Bearer ${cookie.info}`
                },
                withCredentials: true
            });

            const savedFile = response.data?.data as ManualFile;

            if (savedFile) {
                setFiles(prev => [...prev, savedFile]);
                setNotification(old => ({
                    ...old,
                    message: t('manual.uploadSuccess'),
                    type: 'success'
                }));
                setCustomDisplayName('');
                setScreen('list');
            }
        } catch {
            setNotification(old => ({
                ...old,
                message: t('manual.uploadError'),
                type: 'critical'
            }));
        } finally {
            setLoading(false);
            refresh();
        }
    };

    const hasFiles =
        homePageFiles.length > 0 ||
        repairCenterFiles.length > 0 ||
        sparePartsFiles.length > 0 ||
        hslFiles.length > 0 ||
        manufaturaFiles.length > 0 ||
        bslBrFiles.length > 0;

    return (
        <div>
            {isEditable && (
                <div
                    style={{
                        marginBottom: '1rem',
                        display: 'flex',
                        justifyContent: 'end',
                        paddingBottom: '10px'
                    }}
                >
                    <Button
                        type="primary"
                        onClick={() =>
                            screen === 'upload'
                                ? backToList()
                                : openUploadScreen()
                        }
                        className="upload-button"
                    >
                        {screen === 'upload'
                            ? t('manual.cancel')
                            : t('manual.add')}
                    </Button>
                </div>
            )}

            {screen === 'upload' ? (
                <div>
                    <ModalMensagemPortal
                        customDisplayName={customDisplayName}
                        setCustomDisplayName={setCustomDisplayName}
                        t={t}
                        onImageSelected={setSelectedFile}
                        onSelectedSection={setSelectedSection}
                        onSelectedLanguage={setSelectedLanguage}
                    />

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '8px',
                            marginTop: '16px'
                        }}
                    >
                        <Button
                            type="secondary"
                            content={t('manual.cancel')}
                            onClick={backToList}
                        />

                        <Button
                            type="inline"
                            content={t('manual.confirm')}
                            disabled={!isValid}
                            loading={loading}
                            onClick={handleUpload}
                        />
                    </div>
                </div>
            ) : (
                <>
                    {loading ? (
                        <Loader text={t('manual.loading')} />
                    ) : !hasFiles ? (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '36px',
                                color: '#666'
                            }}
                        >
                            {t('manual.NoPortalMessages')}
                        </div>
                    ) : (
                        < div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px'
                            }}
                        >
                            {homePageFiles.length > 0 ?
                                <FilesTable
                                    title={t("manual.homepage")}
                                    files={homePageFiles}
                                    isEditable={isEditable}
                                    downloadingId={downloadingId}
                                    deletingId={deletingId}
                                    onDownload={downloadFile}
                                    onDelete={handleDelete}
                                /> : []
                            }
                            {repairCenterFiles.length > 0 ?
                                <FilesTable
                                    title={t("manual.RepairCenter")}
                                    files={repairCenterFiles}
                                    isEditable={isEditable}
                                    downloadingId={downloadingId}
                                    deletingId={deletingId}
                                    onDownload={downloadFile}
                                    onDelete={handleDelete}
                                /> : []
                            }
                            {sparePartsFiles.length > 0 ?
                                <FilesTable
                                    title={t("manual.SpareParts")}
                                    files={sparePartsFiles}
                                    isEditable={isEditable}
                                    downloadingId={downloadingId}
                                    deletingId={deletingId}
                                    onDownload={downloadFile}
                                    onDelete={handleDelete}
                                /> : []
                            }
                            {hslFiles.length > 0 ?
                                <FilesTable
                                    title={t("manual.HSL")}
                                    files={hslFiles}
                                    isEditable={isEditable}
                                    downloadingId={downloadingId}
                                    deletingId={deletingId}
                                    onDownload={downloadFile}
                                    onDelete={handleDelete}
                                /> : []
                            }
                            {manufaturaFiles.length > 0 ?
                                <FilesTable
                                    title={t("manual.manufacturing")}
                                    files={manufaturaFiles}
                                    isEditable={isEditable}
                                    downloadingId={downloadingId}
                                    deletingId={deletingId}
                                    onDownload={downloadFile}
                                    onDelete={handleDelete}
                                /> : []
                            }
                            {bslBrFiles.length > 0 ?
                                <FilesTable
                                    title={t("manual.BSLBR")}
                                    files={bslBrFiles}
                                    isEditable={isEditable}
                                    downloadingId={downloadingId}
                                    deletingId={deletingId}
                                    onDownload={downloadFile}
                                    onDelete={handleDelete}
                                /> : []
                            }
                        </div>
                    )}
                </>
            )
            }
        </div >
    )
}

export default PortalMessage;